import { LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import { QRCode } from 'antd';
import cx from 'classnames';
import { useState } from 'react';
import alipayIcon from './alipay.png';
import { navigateTo } from './navigation';
import wechatIcon from './wechat.png';
import styles from './index.module.scss';

// 支付宝电脑网站支付返回的是嵌入式订单码页面（或自动提交表单），只能用 iframe 原样嵌入
export const ALIPAY_QR_WIDTH = 160;

export const PAY_TYPE = { WECHAT: 1, ALIPAY: 2 };

const isUrl = (value) => typeof value === 'string' && /^https?:\/\//i.test(value);

// 按 UA 判断设备（不按屏幕宽度）：手机 / 平板上二维码显示在本机屏幕上，没法用同一台设备扫
const MOBILE_UA = /Mobi|Android|iPhone|iPad|iPod|HarmonyOS|Windows Phone/i;

const currentNavigator = () => (typeof navigator === 'undefined' ? undefined : navigator);

export const isMobileDevice = (nav = currentNavigator()) => {
    const ua = nav?.userAgent || '';
    // iPadOS 的 Safari 默认用 Mac 的 UA，靠触摸点数区分
    const ipadAsMac = /Macintosh/.test(ua) && nav?.maxTouchPoints > 1;
    return MOBILE_UA.test(ua) || ipadAsMac;
};

/** 微信内置浏览器：会屏蔽支付宝付款链接 */
export const isWechatBrowser = (nav = currentNavigator()) => /MicroMessenger/i.test(nav?.userAgent || '');

/**
 * 手机端和墨得问题一样全部用支付宝手机网站支付：不扫码，一个按钮直接拉起支付宝 App。
 * 写作全文 / 单章 / 补差价、答辩PPT、格式精修都走这里。
 */
export const isAlipayRedirect = () => isMobileDevice();

/** 下单接口的 client：手机 H5（后端返回支付宝手机网站支付链接），电脑 PC（返回电脑网站订单码） */
export const payClient = () => (isAlipayRedirect() ? 'H5' : 'PC');

/**
 * 微信里打不开支付宝付款链接，换成站内中转页地址（/alipayGo）：
 * 微信里显示「用浏览器打开」的引导并一直查单；用户在系统浏览器里打开同一个地址时立即跳到支付宝付款。
 * 页面地址必须真实带着付款链接，微信菜单的「在浏览器打开」才会把它带过去。
 */
export const alipayGuideHref = (url, orderNo) => {
    const params = new URLSearchParams({ url });
    if (orderNo) params.set('orderNo', orderNo);
    return `/alipayGo?${params.toString()}`;
};

/**
 * 打开支付宝付款页。新窗口打开，原页面继续查单、到账后自动继续；浏览器拦截新窗口时就在当前页跳转。
 * 微信里没有新窗口一说，整页进中转页。
 * 兼容后端返回自动提交表单（HTML）的情况：新窗口里写入表单自动提交。
 */
export const openAlipay = (form, { orderNo } = {}) => {
    if (typeof form !== 'string' || !form.trim()) return false;
    if (isUrl(form)) {
        if (isWechatBrowser()) {
            navigateTo(alipayGuideHref(form, orderNo));
            return true;
        }
        const win = window.open(form, '_blank');
        if (!win) navigateTo(form);
        return true;
    }
    const win = window.open('', '_blank');
    if (!win) return false;
    win.document.open();
    win.document.write(form);
    win.document.close();
    return true;
};

function Placeholder() {
    return (
        <div className={styles.placeholder}>
            <LoadingOutlined />
            <span>付款码生成中</span>
        </div>
    );
}

// 手机：一个支付宝按钮，订单在弹窗打开时已下好，点击时同步打开不会被拦截。
// 微信里按钮照常显示，点了进中转页按提示去浏览器付款（和墨得问题一致）。
function MobileAlipay({ payUrl, orderNo, loading, error, onRetry, className }) {
    const ready = typeof payUrl === 'string' && !!payUrl.trim() && !loading;
    const inWechat = isWechatBrowser();
    return (
        <div className={cx(styles.mobileAlipay, className)}>
            {error ? (
                <button type="button" className={styles.retry} onClick={onRetry}>
                    <ReloadOutlined />
                    <span>获取支付信息失败，点击重试</span>
                </button>
            ) : (
                <button
                    type="button"
                    className={styles.alipayButton}
                    disabled={!ready}
                    onClick={() => openAlipay(payUrl, { orderNo })}
                >
                    {ready ? <img src={alipayIcon} alt="" aria-hidden="true" /> : <LoadingOutlined />}
                    {ready ? '支付宝支付' : '正在准备支付宝…'}
                </button>
            )}
            <div className={styles.mobileHint}>
                {inWechat
                    ? '微信里不能直接打开支付宝，点按钮后按提示在浏览器中付款，付完会自动继续'
                    : '将打开支付宝 App 付款，付完回到本页会自动继续'}
            </div>
        </div>
    );
}

function AlipayCode({ form, size }) {
    const [loaded, setLoaded] = useState(false);
    const scale = size / ALIPAY_QR_WIDTH;
    const frameProps = {
        title: '支付宝付款码',
        width: ALIPAY_QR_WIDTH,
        height: ALIPAY_QR_WIDTH,
        scrolling: 'no',
        frameBorder: 0,
        className: styles.alipayFrame,
        style: scale === 1 ? undefined : { transform: `scale(${scale})` },
        onLoad: () => setLoaded(true),
    };
    return (
        <div className={styles.alipayClip} style={{ width: size, height: size }}>
            {isUrl(form) ? (
                <iframe src={form} {...frameProps} />
            ) : (
                <iframe srcDoc={form} sandbox="allow-scripts allow-forms" {...frameProps} />
            )}
            {!loaded && (
                <div className={styles.frameCover}>
                    <Placeholder />
                </div>
            )}
        </div>
    );
}

function PayChannel({ brand, icon, title, hint, ready, loading, error, onRetry, size, children }) {
    let body = children;
    if (error) {
        body = (
            <button type="button" className={styles.retry} onClick={onRetry}>
                <ReloadOutlined />
                <span>获取失败，点击重试</span>
            </button>
        );
    } else if (loading || !ready) {
        body = <Placeholder />;
    }
    return (
        <div className={cx(styles.channel, styles[brand])}>
            <div className={styles.channelHead}>
                <img src={icon} alt="" aria-hidden="true" />
                <span>{title}</span>
            </div>
            <div className={styles.codeFrame} style={{ width: size, height: size }}>
                {body}
            </div>
            <div className={styles.channelHint}>{hint}</div>
        </div>
    );
}

/**
 * 电脑：支付宝、微信付款码并排（同一个订单号的两个通道，扫哪个付哪个）。
 * 手机：只有一个「支付宝支付」按钮（usePayOrder 在手机上只下支付宝手机网站支付单，alipay.payUrl 是付款链接）。
 * alipay / wechat 形如 { loading, error, form | payUrl | codeUrl }；orderNo 是付款单号（微信中转页查单用，可不传）。
 */
export default function PayQRCodes({ alipay = {}, wechat = {}, orderNo, onRetry, size = 140, className }) {
    if (isAlipayRedirect()) {
        return (
            <MobileAlipay
                payUrl={alipay.payUrl || alipay.form}
                orderNo={orderNo}
                loading={alipay.loading}
                error={alipay.error}
                onRetry={() => onRetry?.(PAY_TYPE.ALIPAY)}
                className={className}
            />
        );
    }
    return (
        <div className={cx(styles.payQRCodes, className)}>
            <PayChannel
                brand="alipay"
                icon={alipayIcon}
                title="支付宝"
                hint="支付宝扫一扫"
                ready={!!alipay.form}
                loading={alipay.loading}
                error={alipay.error}
                onRetry={() => onRetry?.(PAY_TYPE.ALIPAY)}
                size={size}
            >
                <AlipayCode key={alipay.form} form={alipay.form} size={size} />
            </PayChannel>
            <PayChannel
                brand="wechat"
                icon={wechatIcon}
                title="微信支付"
                hint="微信扫一扫"
                ready={!!wechat.codeUrl}
                loading={wechat.loading}
                error={wechat.error}
                onRetry={() => onRetry?.(PAY_TYPE.WECHAT)}
                size={size}
            >
                <QRCode
                    value={wechat.codeUrl || ' '}
                    size={size}
                    bordered={false}
                    bgColor="#fff"
                    className={styles.wechatCode}
                />
            </PayChannel>
        </div>
    );
}
